import React from 'react';
import Icon from './Icon';
import './ProfilePdfComparison.css';

export const VERDICTS = {
  match: { label: 'Same person', icon: 'check', tone: 'good' },
  likely: { label: 'Probably the same person', icon: 'alert-triangle', tone: 'warn' },
  mismatch: { label: 'May be someone else', icon: 'alert-triangle', tone: 'bad' },
  new: { label: 'A new person', icon: 'plus', tone: 'new' }
};

const STATUS = {
  match: { icon: 'check', tone: 'good', text: 'Same' },
  fits: { icon: 'check', tone: 'good', text: 'Fits the masked surname' },
  short: { icon: 'check', tone: 'good', text: 'The same first name, shortened' },
  similar: { icon: 'check', tone: 'good', text: 'Similar' },
  partial: { icon: 'alert-triangle', tone: 'warn', text: 'Partly the same' },
  past: { icon: 'alert-triangle', tone: 'warn', text: 'A past employer in the PDF' },
  differs: { icon: 'alert-triangle', tone: 'warn', text: 'Different' },
  mismatch: { icon: 'x', tone: 'bad', text: 'Different' },
  new: { icon: null, tone: 'new', text: 'Only in the PDF' },
  missing: { icon: null, tone: 'none', text: 'Not in the PDF' }
};

const EMPTY = '—';

const isEmpty = (value) => value === null || value === undefined || value === '' ||
  (Array.isArray(value) && value.length === 0);

// A value as shown in the comparison: profile links without the scheme, long text shortened
const shown = (field, value) => {
  if (isEmpty(value)) return EMPTY;
  if (Array.isArray(value)) {
    if (field === 'work_experience_structured') return `${value.length} role${value.length === 1 ? '' : 's'}`;
    if (field === 'education') return `${value.length} school${value.length === 1 ? '' : 's'}`;
    // Languages come as {name, proficiency}
    return value.map(item => (item && typeof item === 'object' ? item.name : item)).filter(Boolean).join(', ');
  }
  const text = String(value);
  if (/^https?:\/\//.test(text)) return text.replace(/^https?:\/\/(www\.)?/, '');
  return text.length > 90 ? `${text.slice(0, 87)}…` : text;
};

const ACTIONS = { link: 'Link to', replace: 'Replace with', add: 'Add', fill: 'Add' };

/**
 * A LinkedIn PDF compared with a person on file: the verdict, each field side by side, and
 * what saving would change.
 */
const ProfilePdfComparison = ({ review }) => {
  if (!review) return null;
  const verdict = VERDICTS[review.verdict] || VERDICTS.likely;
  const changes = review.changes || [];

  return (
    <div className="pdf-review">
      <div className={`pdf-verdict pdf-verdict--${verdict.tone}`}>
        <Icon name={verdict.icon} />
        <div>
          <strong>{verdict.label}</strong>
          {review.reasons?.length > 0 && (
            <div className="pdf-verdict__reasons">{review.reasons.join(' · ')}</div>
          )}
        </div>
      </div>

      {review.blocked && (
        <p className="pdf-blocked" role="note"><Icon name="alert-triangle" /> {review.blocked}</p>
      )}

      {(review.comparison || []).length > 0 && (
      <table className="pdf-compare">
        <thead>
          <tr>
            <th scope="col"><span className="pdf-sr-only">Field</span></th>
            <th scope="col">On file</th>
            <th scope="col">In PDF</th>
          </tr>
        </thead>
        <tbody>
          {(review.comparison || []).map(row => {
            const status = STATUS[row.status] || STATUS.missing;
            return (
              <tr key={row.field} className={`pdf-compare__row pdf-compare__row--${status.tone}`}>
                <th scope="row">{row.label}</th>
                <td>{shown(row.field, row.on_file)}</td>
                <td>
                  <span className="pdf-compare__pdf">
                    {status.icon && <Icon name={status.icon} className="pdf-compare__status" />}
                    <span>{shown(row.field, row.in_pdf)}</span>
                    <span className="pdf-sr-only"> ({status.text})</span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      )}

      <div className="pdf-changes">
        {changes.length === 0 ? (
          <p className="pdf-changes__none">Nothing new to save from this PDF.</p>
        ) : (
          <>
            <div className="pdf-changes__title">{review.verdict === 'new' ? 'Saving adds them with' : 'Saving will'}</div>
            <ul>
              {changes.map(change => (
                <li key={change.field}>
                  <strong>{change.label}:</strong>{' '}
                  {isEmpty(change.old) || change.action === 'link' || change.action === 'add' ? (
                    <>{ACTIONS[change.action] || 'Set'} {shown(change.field, change.new)}</>
                  ) : (
                    <>{shown(change.field, change.old)} <span aria-hidden="true">→</span><span className="pdf-sr-only"> becomes </span> {shown(change.field, change.new)}</>
                  )}
                  {change.records > 1 && <span className="pdf-changes__meta"> · {change.records} records</span>}
                  {change.note && <div className="pdf-changes__note">{change.note}</div>}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePdfComparison;
